/* engine/worktopRuns.js — WORKTOP RUNS (Phase 4, 2026-07-30)
 *
 * Real kitchens don't get one worktop slab per cabinet: adjacent base units on
 * a wall share ONE continuous run, with factory seams only where the sheet
 * length runs out. This module owns the PURE grouping math; each surface
 * (orbit specs, Present, drawings SVG) builds its own boxes from the groups so
 * every view agrees on run boundaries and seam positions.
 *
 * Grouping contract (deterministic):
 *  · members are base cabinets on the SAME wall with the SAME countertop pick,
 *    the SAME slab band (top height / depth / face offset — folded into hKey)
 *    and gaps ≤ RUN_GAP_M (5 mm) along the wall axis;
 *  · anything non-standard stays SOLO and keeps its per-cabinet slab: CAD-cut
 *    instances (their counter is clipped), elevated units (different band),
 *    GLB-modelled instances whose model VERIFIABLY owns its top (the role
 *    analysis found a worktop part, or a worktop component GLB is attached) —
 *    real manufacturer carcasses are open-topped and group like anyone else
 *    (2026-08-05 ownership inversion);
 *  · only groups of ≥ 2 become runs — single cabinets render byte-identically
 *    to before this feature.
 *
 * Seams: the technician's sheet length (blob.worktops[].maxLenMm, default
 * 4100 mm) splits a run every maxLen from its start — same positions in 3D
 * (groove) and the elevation drawing (tick).
 */

// 2026-08-18: ONE gap tolerance + sheet default — re-exported from the
// surfaces oracle instead of drift-prone local copies
export { RUN_GAP_M, DEF_SHEET_MM as DEFAULT_SHEET_MM } from "./surfaces";
import { RUN_GAP_M, DEF_SHEET_MM as DEFAULT_SHEET_MM } from "./surfaces";

/** Sheet length (metres) for a countertop NAME from the technician catalog. */
export function sheetLenM(blob, ctName) {
  const wt = ctName ? (((blob || {}).worktops) || []).find((x) => x && x.name === ctName) : null;
  const mm = wt && Number(wt.maxLenMm);
  return (Number.isFinite(mm) && mm >= 300 ? mm : DEFAULT_SHEET_MM) / 1000;
}

/** Seam axis positions inside [s0, s1] every maxLenM from the run start. */
export function runSeams(s0, s1, maxLenM) {
  const out = [];
  if (!(maxLenM > 0.05)) return out;
  for (let s = s0 + maxLenM; s < s1 - 0.01; s += maxLenM) out.push(s);
  return out;
}

/** worktopRunGroups(items) → runs of ≥2 members.
 *  items: [{ id, wallId, s0, s1 (m along the wall axis), hKey (slab band
 *  signature), ctKey (countertop name), solo (true = never groups) }]
 *  Returns [{ wallId, ctKey, hKey, s0, s1, members: [ids in axis order] }]. */
export function worktopRunGroups(items) {
  const buckets = new Map();
  (items || []).forEach((it) => {
    if (!it || it.solo || !it.wallId) return;
    const key = it.wallId + "" + it.ctKey + "" + it.hKey;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(it);
  });
  const runs = [];
  buckets.forEach((arr) => {
    arr.sort((a, b) => a.s0 - b.s0);
    let cur = null;
    const flush = () => {
      if (cur && cur.members.length >= 2) runs.push(cur);
      cur = null;
    };
    arr.forEach((it) => {
      if (cur && it.s0 - cur.s1 <= RUN_GAP_M) {
        cur.s1 = Math.max(cur.s1, it.s1);
        cur.members.push(it.id);
      } else {
        flush();
        cur = { wallId: it.wallId, ctKey: it.ctKey, hKey: it.hKey, s0: it.s0, s1: it.s1, members: [it.id] };
      }
    });
    flush();
  });
  runs.sort((a, b) => (a.wallId < b.wallId ? -1 : a.wallId > b.wallId ? 1 : a.s0 - b.s0));
  return runs;
}
